import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, GripVertical, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { EvaluationFormWithItems, EvaluationFormItem } from "@/types/evaluation-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { reportUserError } from "@/lib/report-error";

interface FormularioFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  formulario?: EvaluationFormWithItems | null;
}

interface FormItemDraft {
  id?: number;
  label: string;
  tempId: string;
}

export function FormularioFormModal({
  isOpen,
  onClose,
  onSuccess,
  formulario,
}: FormularioFormModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<FormItemDraft[]>([]);
  const [newItemLabel, setNewItemLabel] = useState("");

  const isEditing = !!formulario;

  useEffect(() => {
    if (formulario) {
      setName(formulario.name);
      setDescription(formulario.description || "");
      setIsActive(formulario.is_active);
      setItems(
        formulario.items?.map((item) => ({
          id: item.id,
          label: item.label,
          tempId: `existing-${item.id}`,
        })) || []
      );
    } else {
      setName("");
      setDescription("");
      setIsActive(true);
      setItems([]);
    }
    setNewItemLabel("");
  }, [formulario, isOpen]);

  const handleAddItem = () => {
    if (!newItemLabel.trim()) return;
    
    setItems([
      ...items,
      {
        label: newItemLabel.trim(),
        tempId: `new-${Date.now()}`,
      },
    ]);
    setNewItemLabel("");
  };

  const handleRemoveItem = (tempId: string) => {
    setItems(items.filter((item) => item.tempId !== tempId));
  };

  const handleMoveItem = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === items.length - 1)
    ) {
      return;
    }

    const newItems = [...items];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setItems(newItems);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({
        title: "Erro",
        description: "O nome do formulário é obrigatório.",
        variant: "destructive",
      });
      return;
    }

    if (items.length === 0) {
      toast({
        title: "Erro",
        description: "Adicione pelo menos um item ao formulário.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditing && formulario) {
        // Atualizar formulário existente
        const { error: formError } = await supabase
          .from("evaluation_forms")
          .update({
            name: name.trim(),
            description: description.trim() || null,
            is_active: isActive,
          })
          .eq("id", formulario.id);

        if (formError) throw formError;

        // Deletar itens removidos
        const existingIds = items.filter((i) => i.id).map((i) => i.id);
        const originalIds = formulario.items?.map((i) => i.id) || [];
        const idsToDelete = originalIds.filter((id) => !existingIds.includes(id));

        if (idsToDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from("evaluation_form_items")
            .delete()
            .in("id", idsToDelete);

          if (deleteError) throw deleteError;
        }

        // Atualizar/criar itens
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.id) {
            // Atualizar item existente
            const { error } = await supabase
              .from("evaluation_form_items")
              .update({
                label: item.label,
                display_order: i + 1,
              })
              .eq("id", item.id);

            if (error) throw error;
          } else {
            // Criar novo item
            const { error } = await supabase
              .from("evaluation_form_items")
              .insert({
                form_id: formulario.id,
                label: item.label,
                field_type: "range_1_10",
                display_order: i + 1,
                min_value: 1,
                max_value: 10,
                is_required: true,
              });

            if (error) throw error;
          }
        }

        toast({
          title: "Formulário atualizado",
          description: "O formulário foi atualizado com sucesso.",
        });
      } else {
        // Criar novo formulário
        const { data: formData, error: formError } = await supabase
          .from("evaluation_forms")
          .insert({
            name: name.trim(),
            description: description.trim() || null,
            is_active: isActive,
            created_by: user?.id,
          })
          .select()
          .single();

        if (formError) throw formError;

        // Criar itens
        const itemsToInsert = items.map((item, index) => ({
          form_id: formData.id,
          label: item.label,
          field_type: "range_1_10",
          display_order: index + 1,
          min_value: 1,
          max_value: 10,
          is_required: true,
        }));

        const { error: itemsError } = await supabase
          .from("evaluation_form_items")
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;

        toast({
          title: "Formulário criado",
          description: "O formulário foi criado com sucesso.",
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error("Erro ao salvar formulário:", error);
      toast({
        title: "Erro",
        description: reportUserError(error, { source: "formulario-form-modal" }).description || "Não foi possível salvar o formulário.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddItem();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Formulário" : "Novo Formulário de Avaliação"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Formulário *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Criativo, Desenvolvedor, Designer..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição opcional do formulário..."
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="is_active">Formulário Ativo</Label>
              <p className="text-sm text-muted-foreground">
                Formulários inativos não aparecem para seleção
              </p>
            </div>
            <Switch
              id="is_active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Itens do Formulário</Label>
              <Badge variant="outline">{items.length} itens</Badge>
            </div>

            <div className="flex gap-2">
              <Input
                value={newItemLabel}
                onChange={(e) => setNewItemLabel(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nome do item (ex: Prêmios, Craft...)"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddItem}
                disabled={!newItemLabel.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {items.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Adicione pelo menos um item ao formulário. Cada item será um critério
                  de avaliação com escala de 1 a 10.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2 border rounded-lg p-3">
                {items.map((item, index) => (
                  <div
                    key={item.tempId}
                    className="flex items-center gap-2 p-2 bg-muted/50 rounded-md"
                  >
                    <div className="flex flex-col gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleMoveItem(index, "up")}
                        disabled={index === 0}
                      >
                        <GripVertical className="h-3 w-3 rotate-90" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleMoveItem(index, "down")}
                        disabled={index === items.length - 1}
                      >
                        <GripVertical className="h-3 w-3 rotate-90" />
                      </Button>
                    </div>
                    <Badge variant="secondary" className="w-6 justify-center">
                      {index + 1}
                    </Badge>
                    <span className="flex-1 font-medium">{item.label}</span>
                    <Badge variant="outline" className="text-xs">
                      1-10
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleRemoveItem(item.tempId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? "Salvando..."
              : isEditing
              ? "Salvar Alterações"
              : "Criar Formulário"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

